import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';

/**
 * Servicio para precargar las traducciones del módulo de autenticación.
 * Esto mejora la UX al tener las traducciones listas antes de mostrar el login.
 */
@Injectable({ providedIn: 'root' })
export class AuthTranslationPreloaderService {
  private readonly _translateService = inject(TranslateService);
  private _preloadCache$: Observable<boolean> | null = null;

  /**
   * Precarga las traducciones necesarias para el módulo de auth.
   * Usa caché para evitar recargas múltiples.
   */
  preloadAuthTranslations(): Observable<boolean> {
    // Si ya existe una precarga en curso o completada, retornar del caché
    if (this._preloadCache$) {
      return this._preloadCache$;
    }

    const currentLang = this._translateService.currentLang || this._translateService.getDefaultLang();

    // Precargar las traducciones de auth y common (necesarias para el login)
    this._preloadCache$ = forkJoin([
      this._translateService.getTranslation(currentLang).pipe(take(1)),
      // Asegurar que las keys específicas de auth estén disponibles
      of(this._translateService.instant('AUTH.LOGIN.SIGN_IN')),
      of(this._translateService.instant('AUTH.LOGIN.FORM.EMAIL')),
      of(this._translateService.instant('AUTH.LOGIN.FORM.PASSWORD')),
      of(this._translateService.instant('AUTH.LOGIN.FORM.SUBMIT')),
    ]).pipe(
      map(() => true),
      catchError(() => {
        console.warn('⚠️ Auth translations preload failed, continuing anyway...');
        return of(true);
      }),
      shareReplay(1), // Compartir resultado entre múltiples suscriptores
    );

    return this._preloadCache$;
  }

  /**
   * Limpia el caché de precarga (útil para testing o cuando cambia el idioma)
   */
  clearCache(): void {
    this._preloadCache$ = null;
  }
}

